import React, { useEffect, useMemo } from "react";
import { Outlet, useParams } from "react-router";
import {
  EventIdPublic,
  EventIdPublicOrSlug,
  EventSlug,
} from "@shared/schemas/branded-id.schemas";
import type { LocaleCode } from "@shared/schemas/locale.schemas";
import { useAnonymousActions } from "@frontend/shared/features/actions/application/actions/use-anonymous-actions";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import { i18n } from "@frontend/i18n";
import {
  readBrowserLocale,
  resolveLocaleFrom,
} from "@frontend/shared/infrastructure/i18n/i18n-locale-resolver";
import { useEventSubdomain } from "../hooks/useEventSubdomain";
import { usePreferredUrlRedirect } from "../hooks/usePreferredUrlRedirect";

interface EventRouteLayoutBaseProps {
  /**
   * The language this audience wants, before the event gets to clamp it. Only
   * the candidate differs by audience — see the two wrappers below; the rule
   * applied to it is the same one.
   */
  readonly localeCandidate: LocaleCode | null;
}

/**
 * Routing-only layout for event-scoped routes. Replaces the routing role of
 * `EventContextProvider`: it resolves the event id/slug from the subdomain or
 * URL param, fetches the PUBLIC event view (no auth needed for URL resolution)
 * via `useAnonymousActions`, applies the preferred-URL redirect, and sets the
 * i18n locale for the event, gating render while those resolve. Data access,
 * auth-aware fetching, the current participant and navigation are the child
 * pages' concern (their own actions hooks) — this component carries no event
 * context.
 *
 * `i18n.setLocale` runs in the render body rather than an effect: `I18n` is a
 * non-reactive singleton that broadcasts nothing, so the locale has to be set
 * before children first read a translation. That is sound only because this
 * component returns `null` until the event query settles.
 */
function EventRouteLayoutBase({
  localeCandidate,
}: EventRouteLayoutBaseProps): React.JSX.Element | null {
  const { eventIdPublicOrSlug: eventIdParam } = useParams<{
    eventIdPublicOrSlug: string;
  }>();
  const subdomain = useEventSubdomain();
  const isEventSubdomain = subdomain !== null;

  const hasMismatch =
    subdomain !== null &&
    eventIdParam !== undefined &&
    subdomain !== eventIdParam;

  useEffect(() => {
    if (hasMismatch) {
      applicationLogger.warn(
        "Subdomain mismatch detected",
        "EventRouteLayout",
        [{ subdomain, routeEventId: eventIdParam }],
      );
    }
  }, [hasMismatch, subdomain, eventIdParam]);

  const eventIdPublicOrSlug = eventIdParam ?? subdomain ?? "";
  const eventIdForFetch = useMemo(
    () => EventIdPublicOrSlug.unsafeCreate(eventIdPublicOrSlug),
    [eventIdPublicOrSlug],
  );

  const { useGetEventPublicView } = useAnonymousActions();
  const eventQuery = useGetEventPublicView({
    eventIdPublicOrSlug: eventIdForFetch,
    enabled: eventIdPublicOrSlug.length > 0 && !hasMismatch,
  });

  const rawIdPublicValue = eventQuery.data?.idPublic ?? null;
  const rawIdPublic =
    rawIdPublicValue === null ? null : EventIdPublic.unwrap(rawIdPublicValue);
  const rawSlugValue = eventQuery.data?.slug ?? null;
  const rawSlug = rawSlugValue === null ? null : EventSlug.unwrap(rawSlugValue);

  const eventIdPublic = useMemo(
    () =>
      rawIdPublic !== null
        ? EventIdPublic.unsafeCreate(rawIdPublic)
        : EventIdPublic.unsafeCreate(eventIdPublicOrSlug),
    [rawIdPublic, eventIdPublicOrSlug],
  );
  const eventSlug = useMemo(
    () => (rawSlug !== null ? EventSlug.unsafeCreate(rawSlug) : null),
    [rawSlug],
  );

  const redirectGate = usePreferredUrlRedirect(
    isEventSubdomain,
    eventIdPublic,
    eventSlug,
    rawIdPublic,
    eventIdParam,
  );

  useEffect(() => {
    if (eventQuery.isError) {
      applicationLogger.warn(
        "Event fetch failed; keeping the locale already in effect, which may not be one this event declares",
        "EventRouteLayout",
        [{ eventIdPublicOrSlug }],
      );
    }
  }, [eventQuery.isError, eventIdPublicOrSlug]);

  // Only a loaded event can decide the locale. On error the languages are
  // unknown, so there is no value to clamp to and the in-effect locale stands.
  if (!eventQuery.isLoading && !eventQuery.isError) {
    i18n.setLocale(
      resolveLocaleFrom(localeCandidate, eventQuery.data?.languages),
    );
  }

  if (
    eventIdPublicOrSlug === "" ||
    hasMismatch ||
    eventQuery.isLoading ||
    redirectGate.shouldBlock
  ) {
    return null;
  }

  return <Outlet context={{ eventSubdomain: subdomain }} />;
}

/**
 * Event routes nobody is signed in for — the join / event-landing pages.
 * Rule 4-anonymous: the browser's language when the event declares it, else the
 * event's primary. The trigger phrase handed to WhatsApp from here becomes
 * Porter's first inbound message, so this choice seeds the conversation.
 */
export function AnonymousEventRouteLayout(): React.JSX.Element | null {
  return <EventRouteLayoutBase localeCandidate={readBrowserLocale()} />;
}

/**
 * Event routes behind `AuthenticatedRouteGuard`. Rule 4-authenticated: the
 * member's `preferredLocale` when the event declares it, else the event's
 * primary — the same value Porter resolves, so chat and pages agree.
 *
 * Reading the user here costs no request: the guard above already awaited
 * `/users/me` and renders children only once it resolved, so this hook hits a
 * warm cache. The guard is also what stops a first paint at the wrong locale —
 * the singleton is not reactive, so a locale set too early could never be
 * corrected.
 */
export function AuthenticatedEventRouteLayout(): React.JSX.Element | null {
  const { user } = useUserActions();

  return (
    <EventRouteLayoutBase localeCandidate={user?.preferredLocale ?? null} />
  );
}
